import { Component, Input, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DateEx } from '../../../functions/date-ex';
import { rememberTaskSearchUpdate } from '../../../functions/todo/search-retain-updates';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { TaskRequest } from '../../../models/api/todo/task-request';
import { ClaimRow } from '../../../models/api/view/claim-view/claim-row';
import { TasksService } from '../../../services/tasks/tasks-service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { PersonalTaskDataService } from '../../../services/todo/personal-task-data.service';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { DatePickerComponent } from '../../core/controls/date-picker.component';
import { InputComponent } from '../../core/controls/input.component';
import { TextareaComponent } from '../../core/controls/textarea.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { DetailItemComponent } from '../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { RequiredFieldsNoteComponent } from '../../core/labels/required-fields-note.component';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-task-edit-page',
  imports: [
    LoadingPanelComponent,
    EmptyStateComponent,
    InputComponent,
    ButtonComponent,
    DatePickerComponent,
    CheckboxComponent,
    TextareaComponent,
    ActionRowComponent,
    ReactiveFormsModule,
    RouterModule,
    PageHeaderComponent,
    RequiredFieldsNoteComponent,
    DetailPanelComponent,
    DetailItemComponent,
  ],
  template: `
    <app-page-header
      [label]="headerLabel"
      [backRouterLink]="backRouterLink"
      [backLabel]="backLabel"
    />
    <app-loading-panel
      [state]="state"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
    >
      <ng-template #empty>
        <app-empty-state icon="fa-history" label="Empty history" height="450px">
          <span>TODO empty state</span>
        </app-empty-state>
      </ng-template>

      <ng-template #loaded>
        <form [formGroup]="fg">
          <app-required-fields-note />
          <div class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-2 lg:tw-gap-0">
            <div
              class="tw-flex tw-flex-col tw-w-full lg:tw-w-1/3 tw-min-w-0 tw-gap-2 lg:tw-gap-0"
            >
              <app-input
                class="tw-w-full"
                [control]="fg.controls.title"
                label="Title"
                [maxlength]="100"
              />
              <app-date-picker
                class="tw-w-full"
                [control]="fg.controls.dueDate"
                label="Due Date"
              />
              <app-checkbox
                label="Send Email Reminder"
                [control]="fg.controls.sendEmail"
              />
              @if (headerLabel !== 'New Task') {
                <app-checkbox
                  label="Completed"
                  [control]="fg.controls.completed"
                />
              }
            </div>
            <div
              class="tw-flex tw-flex-col tw-w-full lg:tw-w-2/3 tw-min-w-0 lg:tw-pl-4"
            >
              <app-textarea
                [control]="fg.controls.defaultText"
                label="Description"
                rows="16"
              ></app-textarea>
              @if (isEditMode) {
                <app-detail-panel
                  class="tw-w-full"
                  [dataSource]="taskDates"
                  [row]="true"
                  ariaLabel="Task dates"
                >
                  <app-detail-item
                    label="Created on"
                    name="createdOn"
                    format="d"
                    classes="tw-basis-full tw-justify-end"
                  />
                  <app-detail-item
                    label="Updated on"
                    name="updatedOn"
                    format="d"
                    classes="tw-basis-full tw-justify-end"
                    [showEmpty]="false"
                  />
                </app-detail-panel>
              }
            </div>
          </div>
          <app-action-row useMobileLayout>
            <app-button
              [disabled]="!fg.valid"
              right1
              type="submit"
              [loading]="loading"
              label="Save Task"
              (click)="saveTask()"
            />
            <app-button
              right2
              emphasis="Low"
              [routerLink]="backRouterLink"
              label="Cancel"
            ></app-button>
          </app-action-row>
        </form>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class TaskEditPageComponent extends BaseComponent implements OnInit {
  private tasksService = inject(TasksService);
  private personalTaskData = inject(PersonalTaskDataService);
  private snackbar = inject(Snackbar);

  @Input()
  loading = false;

  backRouterLink = '/todo?tab=search';
  backLabel = 'My to-do';
  headerLabel = 'New Task';

  isEditMode = false;
  isPageTodo = false;
  isClaimSpecific = false;

  claim: ClaimRow;
  taskUID: number = null;
  recordID: string = null;
  recordSource: string = null;

  protected state: LoadingState = 'Loading';
  protected taskDates: { createdOn: Date; updatedOn: Date } = {
    createdOn: null,
    updatedOn: null,
  };

  fg = new FormGroup({
    title: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(100),
    ]),
    dueDate: new FormControl<Date>(null, [Validators.required]),
    completed: new FormControl<boolean>(false),
    sendEmail: new FormControl<boolean>(false),
    defaultText: new FormControl<string>(null, [
      Validators.required,
      Validators.maxLength(10000),
    ]),
  });

  async ngOnInit() {
    const data = this.storage.get('current-task');
    this.backRouterLink = data?.backRouterLink || '/todo';
    this.backLabel = data?.backLabel || 'My to-do';
    this.isPageTodo = data?.backLabel === 'My to-do';
    this.isClaimSpecific = data?.isClaimSpecific || false;
    if (this.isClaimSpecific && !data?.title) {
      this.claim = this.view.currentClaim();
      this.headerLabel = 'New Task';
    }

    if (data?.title) {
      this.isEditMode = true;
      this.recordID = data.recordId || null;
      this.recordSource = data.recordSource || null;
      this.taskUID = data.taskUid;
      this.headerLabel = 'Edit Task';
      this.fg.controls.title.setValue(data.title);
      this.fg.controls.dueDate.setValue(
        data.dueDate ? new Date(data.dueDate) : null,
      );
      this.fg.controls.defaultText.setValue(data.message);
      this.fg.controls.completed.setValue(data.status === 'Completed');
      this.fg.controls.sendEmail.setValue(data.sendEmail);
      const created = this.asDate(data.dateCreated);
      const updated = this.asDate(data.dateUpdated);
      this.taskDates = {
        createdOn: created,
        updatedOn:
          updated && (!created || updated.getTime() !== created.getTime())
            ? updated
            : null,
      };
    } else if (!this.isPageTodo && !this.isClaimSpecific) {
      this.claim = this.view.currentClaim();
    }
    this.state = 'Loaded';
  }

  private asDate(value: Date | string): Date {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  saveTask() {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    if (this.isEditMode && this.fg.value.completed) {
      this.confirm.show({
        label: 'Mark task complete',
        width: '35rem',
        message: 'Are you sure you want to mark 1 task complete?',
        yesLabel: 'Yes',
        noLabel: 'No',
        confirm: () => this.persistTask(),
      });
      return;
    }

    this.persistTask();
  }

  @HandleLoadingState({ loadingProperty: 'loading' })
  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  private async persistTask() {
    const formValue = this.fg.value;
    const data = this.storage.get('current-task');

    let claimDetails = {};
    if (this.isEditMode) {
      if (this.recordID != null) {
        claimDetails = this.getClaimSpecificFields(
          this.recordSource,
          this.recordID,
        );
      }
    } else {
      if (this.isClaimSpecific) {
        claimDetails = {
          clientNumber: data?.contractNumber || this.claim?.contractNumber,
          ...this.getClaimSpecificFields(),
        };
      }
    }
    const dueDate = DateEx.startOfLocalDay(formValue.dueDate);
    const taskRequest: TaskRequest = {
      taskUID: this.taskUID,
      title: formValue.title,
      dueDate,
      sendEmail: formValue.sendEmail,
      completed: formValue.completed,
      description: formValue.defaultText,
      forUser: !this.claim,
      emailReminder: !!formValue.sendEmail,
      recordSource: this.claim?.recordSource || this.recordSource,
      recordId: this.claim?.recordId || undefined,
      ...claimDetails,
    };

    if (this.isEditMode) {
      await this.api.task.updateTask(taskRequest);
    } else {
      if (this.isClaimSpecific || (!this.isPageTodo && this.claim)) {
        await this.api.task.createTaskforClaim(taskRequest);
      } else {
        await this.api.task.createTask(taskRequest);
      }
    }
    if (this.isClaimSpecific) {
      this.storage.set(
        'todo-show-personal-tasks',
        'claim-specific-task-creation',
      );
    }

    // clear claim-specific cache
    if (this.claim) {
      this.tasksService.deleteTasksByClaim({
        recordId: this.claim.recordId,
        recordSource: this.claim.recordSource,
      });
    }

    if (this.isEditMode && this.taskUID != null) {
      rememberTaskSearchUpdate(this.storage, {
        taskUid: this.taskUID,
        title: formValue.title ?? '',
        dueDate: formValue.dueDate ?? undefined,
        message: formValue.defaultText ?? '',
        status: formValue.completed ? 'Completed' : 'Open',
        sendEmail: !!formValue.sendEmail,
      });
    }
    const patchKey = this.searchPatchKey(this.backRouterLink);
    this.storage.set(patchKey, {
      isNew: !this.isEditMode,
      taskUID: this.taskUID,
      title: formValue.title,
      dueDate,
      description: formValue.defaultText,
      completed: !!formValue.completed,
      sendEmail: !!formValue.sendEmail,
    });
    this.personalTaskData.notifyChanged();
    if (formValue.completed) {
      this.snackbar.show('Task completed', 'Success');
    }
    this.router.navigate([this.backRouterLink]);
  }

  private searchPatchKey(path: string): string {
    const page = (path || '/todo').split('?')[0];
    return `personal-task-search-patch:${page}`;
  }

  protected onAddUser() {
    // this.nameOptions.push(this.fg.controls.tempUser.value);
    // this.fg.controls.tempUser.setValue('');
    // this.fg.controls.selectedUsers.reset();
  }

  protected onRemoveUser() {
    // this.nameOptions = this.nameOptions.filter((_) => {
    //   return !this.fg.controls.selectedUsers.value.includes(_);
    // });
  }

  protected onRemoveAll() {
    // this.nameOptions = [];
  }

  private getClaimSpecificFields(
    recordSource?: string,
    recordId?: string,
  ): any {
    if (recordSource && recordId !== undefined) {
      return this.buildClaimFields(recordSource, recordId, this.claim);
    }
    const claim = this.view.currentClaim();
    if (!claim) {
      return {
        claimUID: null,
        absenceUID: null,
        adaEventNumber: null,
        leaveNumber: null,
        employeeUID: null,
      };
    }

    return this.buildClaimFields(
      claim.recordSource,
      claim.recordId,
      claim,
      claim.fileNumber,
    );
  }

  private buildClaimFields(
    recordSource: string,
    recordId: string,
    claim: any,
    fileNumber?: string,
  ): any {
    switch (recordSource) {
      case 'J':
        return {
          claimUID: recordId,
          absenceUID: null,
          adaEventNumber: null,
          leaveNumber: null,
          employeeUID: null,
        };
      case 'T':
        return {
          claimUID: null,
          absenceUID: fileNumber || recordId,
          employeeUID: claim?.hrMasterUid ? Number(claim.hrMasterUid) : null,
          adaEventNumber: null,
          leaveNumber: null,
        };
      case 'A':
        return {
          claimUID: null,
          absenceUID: null,
          adaEventNumber: recordId,
          leaveNumber: null,
          employeeUID: null,
        };
      case 'L':
        return {
          claimUID: null,
          absenceUID: null,
          adaEventNumber: null,
          leaveNumber: numericAdaOrLeaveKeyFromClaim(
            claim ?? { fileNumber, recordId },
          ),
          employeeUID: null,
        };
      case 'R':
        return {
          claimUID: null,
          absenceUID: null,
          adaEventNumber: null,
          leaveNumber: null,
          employeeUID: null,
          adaTamsRequestID: recordId,
          adaTAMSEventNumber: fileNumber,
        };
      default:
        return {
          claimUID: recordId,
          absenceUID: null,
          adaEventNumber: null,
          leaveNumber: null,
          employeeUID: null,
        };
    }
  }
}
